import React from 'react';
import { Download, Loader2 } from 'lucide-react';
import { IconButton, Status } from '../ui';

/**
 * GalleryMasonryGrid — Pinterest-like desktop layout.
 *
 * Uses CSS `columns` so images sit at their natural aspect ratio:
 * Story (9:16) cards are tall, Feed (1:1) cards are square.
 *
 * Props:
 *   posts:         Post[]
 *   onPostClick:   (post) => void
 *   onDownload:    (url, filename) => void
 */
const GalleryMasonryGrid = ({ posts, onPostClick, onDownload }) => {
  return (
    <div className='columns-2 lg:columns-3 xl:columns-4 gap-4'>

      {posts.map((post) => {
        const isDraft = post.status === 'draft';
        const src = post.path || post.imageUrl;
        const isStory = post.aspectRatio === '9:16';

        return (
          <div
            key={post.id}
            className='relative break-inside-avoid mb-4 rounded-xl overflow-hidden group'
            style={{
              border:     '1px solid var(--border)',
              background: 'var(--bg-card)',
              cursor:     isDraft ? 'not-allowed' : 'pointer',
            }}
            onClick={() => !isDraft && onPostClick(post)}
          >
            {isDraft ? (
              /* Generating placeholder — maintain aspect ratio */
              <div
                className={`flex flex-col items-center justify-center gap-3 p-8 ${isStory ? 'aspect-[9/16]' : 'aspect-square'}`}
                style={{ background: 'var(--bg-surface)' }}
              >
                <Loader2 className='w-8 h-8 animate-spin' style={{ color: 'var(--brand)' }} />
                <span className='text-xs text-center' style={{ color: 'var(--text-muted)' }}>
                  Generating...
                </span>
              </div>
            ) : (
              <>
                <img
                  src={src}
                  alt='Post content'
                  className='w-full block'
                  loading='lazy'
                  style={{ display: 'block' }}
                />

                {/* Always-visible bottom bar: status + type */}
                <div
                  className='absolute bottom-0 left-0 right-0 flex items-center justify-between gap-2 px-2 py-1.5'
                  style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 100%)' }}
                >
                  <Status status={post.status} />
                  <span
                    className='text-xs font-medium px-2 py-0.5 rounded'
                    style={{
                      background: 'rgba(0,0,0,0.5)',
                      color:      '#fff',
                    }}
                  >
                    {post.aspectRatio === '9:16' ? 'Story' : 'Feed'}
                  </span>
                </div>

                {/* Hover overlay: download button */}
                <div
                  className='absolute inset-0 flex flex-col p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200'
                  style={{ background: 'rgba(0,0,0,0.18)' }}
                >
                  <div className='flex justify-end'>
                    <IconButton
                      icon={Download}
                      onClick={(e) => {
                        e.stopPropagation();
                        onDownload(src, `post-${post.id}`);
                      }}
                      variant='ghostPrimary'
                      tooltip='Download'
                      size='md'
                      tooltipPosition='bottom'
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default GalleryMasonryGrid;
